import {
  Checkbox,
  CIActionBtnGroup,
  CIActionGuard,
  CITableGrid,
  getLocaleDateTimeFormat,
  Loader,
  Modal,
  NoDataAvailable,
  UnableToFetch,
  useToastMessage,
  useUserPermission,
  usePaginationPreference
} from 'ccs-ui-components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { requiredAuthPermission } from '../../../../../common/constants/UserPermissionConstants';
import { EVENT_ASSOCIATION_TABS, EVENT_ROUTE, EventLabels, SORT_LABELS, SORT_STRINGS, STATUS_TYPES } from '../../../../constants';
import { IEvent, IRecordModel } from '../../../../models';
import { ButtonProp, IEventList } from '../../../../models/index';
import useSelectAllCheckBox, { MAX_SELECTION_SIZE } from '../../../../store/actions/actionType/services/UseSelectAllCheckBoxes';
import { fetchEventListRequest } from '../../../../store/actions/sagaActions/events/GetAllEventSagaActions';
import { IEventData } from '../../../../store/actions/types/events/GetAllEventListTypes';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import { addClearData, addFilterEventData, addSortEventData } from '../../../../store/reducer/events/EventFilterReducer';
import {
  defaultEventListStatus,
  getDateValue,
  getFilterParams,
  getSortState,
  getSortValue,
  getTypeValue,
  getValue,
  isQueryParamSortExist
} from '../../../../utilities/events/EventsListUtils';
import { getQueryParamCondition, paginationParamUtil, statusProvider } from '../../../../utilities/services/ServiceListUtils';
import { updatedBy, renderEventStatus, renderLastModified } from './EventListHelper';

import DropdownMenuButton from '../../../../../common/components/UI/menu/DropdownMenuButton';
import { getFromDateOrToDate } from '../../../../../common/utils/CommonUtils';
import { REGISTRY_TYPE } from '../../../../../dashboard/constants/CommonConstants';
import { clearEvent } from '../../../../store/reducer/events/EventReducer';
import classes from './EventList.module.scss';

export const EventList = (props: IEventList) => {
  // props destructuring
  const { selectedExportItem, exportLoading, exportSuccess, onDependencyGraphFound } = props;

  // 3rd party initialization
  const dispatch = useDispatch<AppDispatch>();
  const { t: translate } = useTranslation();
  const { permissionObject } = useUserPermission();
  const route = useLocation();
  const history = useNavigate();

  // custom hooks
  const { failure, renderToast } = useToastMessage();
  const { getPreferredPageSize, persistPageSize } = usePaginationPreference();

  const { events: listEvents } = useSelector(
    (state) => state.fetchAllEventReducer
  );
  const { searchText, status, fromDate, toDate, clearData, sort: reduxSort } = useSelector(
    (state) => state.filterEventReducer
  );

  const { events, eventErrorMessage, isLoading } = useSelector(
    (state) => state.fetchAllEventReducer
  );
  const { errorMessage, success: successCreate, isLoading: createEventLoading } = useSelector((state) => state.eventReducer);

  const params = useMemo(
    () =>
      getFilterParams(route.search, { searchText, status, fromDate, toDate, sort: reduxSort, clearData }),
      [route.search, searchText, status, fromDate, toDate, reduxSort, clearData]
    );

  // states declaration
  const [queryParams, setQueryParams] = useState<{ [key: string]: string }>(
    params
  );
  const [page, setPage] = useState<number>(listEvents.currentPageNumber);
  const [item, setItem] = useState<number>(params.item ? Number(params.item) : getPreferredPageSize());
  // States to manage the export configuration of events
  const [selectedEventList, setSelectedEventList] = useState<IRecordModel[]>(
    []
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [urlEventName, setUrlEventName] = useState('');
  const [urlVersion, setUrlVersion] = useState('');
  const [eventOnEditNavigation, setEventOnEditNavigation] = useState<
    IEventData | undefined
  >(undefined); // to be used on Edit navigation
  const [selectedSortField, setSelectedSortField] = useState(
    queryParams.sort ?? SORT_LABELS.LASTMODIFIED
  );
  const [, setSelectedSortFieldOrder] = useState(
    !!(queryParams.type && queryParams.type === SORT_STRINGS.ASC)
  );
  const [isCheckAllEventList, setIsCheckAllEventList] = useState(false);
  const { handleSelectAllCheckbox, showMaxLimSelectionToast } = useSelectAllCheckBox(
    selectedEventList,
    setIsCheckAllEventList,
    failure,
    setSelectedEventList,
    item
  );

  /**
   * @method navigateToEventPreview
   * @description Navigates to the event preview page with the specified event details.
   * @param eventDetails - The event details to be passed to the preview page.
   */
  const navigateToEventPreview = (eventDetails: IEventData) => {
    history(`${EVENT_ROUTE.EVENTS_VIEW_ROUTE}${eventDetails.eventName}/${eventDetails.version}`, { state: { path: route.search, eventDataFromList: eventDetails } });
  };

  /**
   * @method navigateToAssociations
   * @description Navigates to the associations page with the specified event details.
   * @param eventDetails - The event details to be passed to the preview page.
   */
  const navigateToAssociations = (eventDetails: IEventData) => {
    history(`${EVENT_ROUTE.ASSOCIATIONS_ROUTE}${eventDetails.eventName}/${eventDetails.version}/${EVENT_ASSOCIATION_TABS.SERVICES}`, { state: { path: route.search, eventDataFromList: eventDetails } });
  };

  /**
   * @method handleCloseConfirmDialog
   * @description Closes the confirm dialog.
   */
  const handleCloseConfirmDialog = () => setShowConfirmDialog(false);

  /**
   * @method handleConfirmDialog
   * @description Handles the confirmation dialog for editing an event.
   * Resets the state and navigates to the edit event page with the updated URL parameters.
   */
  const handleConfirmDialog = () => {
    setShowConfirmDialog(false);
    setUrlEventName('');
    setUrlVersion('');
    history(
      `${EVENT_ROUTE.EVENTS_EDIT_ROUTE}${urlEventName}/${urlVersion}`,
      { state: { path: route.search, eventDataFromList: eventOnEditNavigation } }
    );
  };

  /**
   * @method navigateToEventEdit
   * @description Navigates to the event edit page based on the provided event details.
   * If the event status is "ACTIVE", it shows a confirmation dialog before navigating.
   * Otherwise, it directly navigates to the event edit page.
   * @param eventDetails The event details to navigate with.
   */
  const navigateToEventEdit = (eventDetails: IEventData) => {
    const receivedStatus = statusProvider(eventDetails?.status);
    if (receivedStatus?.toLocaleUpperCase() === STATUS_TYPES.ACTIVE || receivedStatus?.toLocaleUpperCase() === STATUS_TYPES.INACTIVE) {
      setShowConfirmDialog(true);
      setUrlEventName(eventDetails?.eventName);
      setUrlVersion(eventDetails?.version);
      setEventOnEditNavigation(eventDetails);
    } else {
      history(
        `${EVENT_ROUTE.EVENTS_EDIT_ROUTE}${eventDetails?.eventName}/${eventDetails?.version}`,
        { state: { path: route.search, eventDataFromList: eventDetails } }
      );
    }
  };

  /**
   * @method onGetPageHandler
   * @description Handles the event when a page number is selected.
   * @param {number} pageNumber - The selected page number.
   */
  const onGetPageHandler = (pageNumber: number) => {
    setPage(pageNumber);
    setIsCheckAllEventList(false);
    setQueryParams((pre) => ({
      ...pre,
      page: String(pageNumber)
    }));
  };

  /**
   * @method onGetItemsHandler
   * @description Handles the event when an item is selected.
   * @param itemNumber - The number of the selected item.
   */
  const onGetItemsHandler = (itemNumber: number) => {
    setItem(itemNumber);
    setQueryParams((pre) => ({
      ...pre,
      item: String(itemNumber)
    }));
  };

  /**
   * @method setFilterStates
   * @description Sets the filter states and dispatches actions to fetch event list based on the filter conditions.
   */
  const setFilterStates = () => {
    dispatch(addClearData(ButtonProp.refresh));
    const sortState = getSortState(queryParams);
    dispatch(
      addSortEventData(
        queryParams.sort !== ''
          ? `${queryParams.sort},${queryParams.type}`
          : SORT_STRINGS.LAST_MODIFIED
      )
    );
    const queryParamCondition = getQueryParamCondition(queryParams);
    const statusParamQuery = status && status.split(',')?.length >= 5 ? '' : queryParamCondition;
    const statusParam = status !== '' || queryParams.status !== undefined
      ? statusParamQuery
      : queryParams.status;
    paginationParamUtil(setPage, setItem, queryParams, getPreferredPageSize());
    if (route.search !== '') {
      const queryParam = {
        searchText: queryParams.eventName ?? '',
        status: statusParam,
        fromDate: getFromDateOrToDate(queryParams.fromDate),
        toDate: getFromDateOrToDate(queryParams.toDate)
      };
      dispatch(addFilterEventData(queryParam));
      dispatch(
        fetchEventListRequest(
          page,
          item,
          queryParam,
          isQueryParamSortExist(queryParams)
            ? sortState
            : `${queryParams.sort},${queryParams.type}`
        )
      );
    } else {
      dispatch(fetchEventListRequest(page, item, queryParams, sortState));
    }
  };

  /**
   * @method sortHandler
   * @description Handles the sorting of the event list based on the selected label and state.
   * @param label - The label to sort by.
   * @param selectedLabelState - The state of the selected label (true for ascending, false for descending).
   */
  const sortHandler = (label: string, selectedLabelState: boolean) => {
    setQueryParams((pre) => ({
      ...pre,
      sort: label,
      type: selectedLabelState ? SORT_STRINGS.ASC : SORT_STRINGS.DESC
    }));
  };

  /**
   * @method handleClick
   * @description Handles the click event on the element.
   * @param e - The click event.
   * @param label - The label associated with the clicked element.
   */
  const handleClick = (e: React.MouseEvent<HTMLElement>, label: string) => {
    if (selectedSortField === label) {
      setSelectedSortFieldOrder((preValue) => {
        sortHandler(selectedSortField, !preValue);
        return !preValue;
      });
    } else {
      setSelectedSortField(label);
      setSelectedSortFieldOrder(true);
      sortHandler(label, true);
    }
  };

  const footerOptionsObj = {
    itemsPerPage: item,
    currentPageNumber: page,
    numberOfPages: listEvents.numberOfPages,
    onCurrentPageNumberChange: onGetPageHandler,
    onItemsPerPageChange: onGetItemsHandler
  };

  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  /**
   * @method handleSelectCheckbox
   * @description Handles the selection of a checkbox in the event list.
   * @param name - The change event of the checkbox.
   * @param version - The version number of the record.
   */
  const handleSelectCheckbox = (
    name: React.ChangeEvent<HTMLInputElement>,
    version: number
  ) => {
    const { id, checked } = name.target;
    if (
      checked
      && selectedEventList.length >= MAX_SELECTION_SIZE
    ) {
      showMaxLimSelectionToast();
    } else {
      setSelectedEventList([
        ...selectedEventList,
        { recordName: id, recordVersion: version }
      ]);
    }
    if (!checked) {
      setSelectedEventList(
        selectedEventList.filter(
          (item) =>
            JSON.stringify(item)
            !== JSON.stringify({ recordName: id, recordVersion: version })
        )
      );
      setIsCheckAllEventList(false);
    }
  };

  /**
   * @description Renders the modal body for the active/inactive events..
   */
  const modalBody = (
    <div data-testid="modal-for-active-inactive">
      {translate('events_wrapper.modal_text')}
    </div>
  );

  /**
   * Gets the updated events
   */
  const onDeleteSuccess = () => {
    onGetPageHandler(0);
  };

  const getCurrentEvent = (row: IEventData) => events.events.find((event) => row.eventName === event.eventName && row.version === event.version);

  const onDependencyGraphFoundForEvent = (isDependencyGraphFound: boolean, registryName: string, entityVersion: string, entityStatus: string) => {
    onDependencyGraphFound(isDependencyGraphFound, registryName, entityVersion, entityStatus);
  };

  /* @method renderActionBtn
   * @description Renders the action buttons for each row in the event list.
   * @param row - The data of the current row.
   * @returns The JSX element containing the action buttons.
   */
  const renderActionBtn = (row: IEventData) => (
    <div className={classes['action-col']}>
      <CIActionBtnGroup
        buttons={[
          {
            label: `${translate('info_message.msg_view_association')}`,
            key: 'dependencyIcon',
            onClick: async () => navigateToAssociations(row),
            testId: 'association-btn',
            disabled: row.status?.toLocaleUpperCase() !== STATUS_TYPES.ACTIVE.toLocaleUpperCase()
          },
          {
            label: 'view',
            key: 'viewIcon',
            onClick: async () => navigateToEventPreview(row),
            testId: 'view-btn'
          },
          {
            label: 'edit',
            key: 'editIcon',
            onClick: async () => navigateToEventEdit(row),
            disabled: row.status?.toLocaleUpperCase() === STATUS_TYPES.PENDING_APPROVAL || row.status?.toLocaleUpperCase() === STATUS_TYPES.INACTIVE,
            show: CIActionGuard(
              [
                requiredAuthPermission.Events.edit,
                requiredAuthPermission.Events.approve
              ],
              permissionObject.permission
            ),
            testId: 'edit-btn'
          }
        ]}
      />
      <DropdownMenuButton
        registryName={row.eventName}
        status={row.status}
        deleteURL={`events/${row.eventName}/version/${row.version}`}
        onDeleteSuccess={onDeleteSuccess}
        showOn="list"
        entityData={getCurrentEvent(row) as unknown as IEvent}
        registryType={REGISTRY_TYPE.EVENTS}
        onDependencyGraphFound={onDependencyGraphFoundForEvent}
      />
    </div>
  );

  /**
   * @method renderEventBody
   * @description Renders the body of an event row.
   * @param row - The data of the event row.
   * @returns The JSX element representing the event body.
   */
  const renderEventBody = (row: IEventData) => (
    <div className={classes['render-event-body']}>
      <Checkbox
        type="checkbox"
        onChange={(e) => handleSelectCheckbox(e, parseInt(row.version, 10))}
        id={row.eventName}
        checked={
          selectedEventList.some(
            (item) =>
              item.recordName === row.eventName
              && item.recordVersion === parseInt(row.version, 10)
          ) && row.status?.toLocaleUpperCase() === STATUS_TYPES.ACTIVE
        }
        disabled={row.status?.toLocaleUpperCase() !== STATUS_TYPES.ACTIVE}
        data-testid="selectCheckBox"
      />
      <div className={classes['body-render']}>
        <div title={row.eventName} className={classes['body-render-subclass']}>
          <p className="event-list-eventname">{row.eventName}</p>
        </div>
        <p className="event-list-version">
          {translate('event_list_component.txt_version', {
            replace: {
              version: row.version
            }
          })}
        </p>
      </div>
    </div>
  );

  useEffect(() => {
    // for event import export logic
    selectedExportItem(selectedEventList); // maintain list of selected events
    if (exportSuccess) {
      // clear exported events once exported
      setSelectedEventList([]);
      selectedExportItem([]);
    }
  }, [selectedEventList, exportSuccess]);

  useEffect(() => {
    if (Object.keys(queryParams)?.length !== 0) {
      const searchParams = new URLSearchParams();
      Object.entries(queryParams).forEach(([k, v]) => {
        if (v) {
          searchParams.set(k, v);
        }
      });

      setFilterStates();
      const queryString = searchParams.toString();
      history(
        queryString ? `?${queryString}` : route.search,
        { state: route.search }
      );
    }
  }, [queryParams]);

  useEffect(() => {
    // to handle page refresh
    if (page >= 0 || clearData !== ButtonProp.refresh) {
      setFilterStates();
    }
  }, [item, page]);

  useEffect(() => {
    switch (clearData) {
      case ButtonProp.clear:
        // Loop when user Clear the filter's
        setPage(0);
        setQueryParams((pre) => ({
          ...pre,
          status: defaultEventListStatus,
          fromDate: '',
          toDate: '',
          page: '',
          item: ''
        }));
        break;
      case ButtonProp.apply:
        // Loop when user Apply the filter's
        setPage(0);
        setQueryParams((pre) => ({
          ...pre,
          sort: getSortValue(queryParams),
          type: getTypeValue(queryParams),
          status: getValue(status ?? ''),
          eventName: getValue(searchText ?? ''),
          fromDate: getDateValue(fromDate),
          toDate: getDateValue(toDate),
          page: String(0) ?? ''
        }));

        break;
      case ButtonProp.search:
        // Loop when user search the result from search event's
        setPage(0);
        setQueryParams((pre) => ({
          ...pre,
          sort: getSortValue(queryParams),
          type: getTypeValue(queryParams),
          eventName: getValue(searchText ?? ''),
          page: String(0) ?? ''
        }));
        break;
      case ButtonProp.refresh:
        if (route.search === '') {
          // Loop when user search refresh the page
          setQueryParams((pre) => ({
            ...pre,
            sort: getSortValue(queryParams),
            type: getTypeValue(queryParams),
            status: getValue(status ?? ''),
            eventName: getValue(searchText ?? ''),
            fromDate: getDateValue(fromDate),
            toDate: getDateValue(toDate)
          }));
        }
        break;
      default:
        break;
    }
  }, [clearData]);

  useEffect(() => {
    if (queryParams.item) {
      persistPageSize(Number(queryParams.item));
    }
  }, [queryParams.item]);

  const showFailureToast = () => {
    failure(
      translate('toast_component.lbl_error'),
      translate('events_wrapper.msg_create_event_fail_body')
    );
  };

  useEffect(() => {
    if (successCreate) {
      dispatch(clearEvent());
    } else if (errorMessage && !successCreate) {
      showFailureToast();
      dispatch(clearEvent());
    }
  }, [errorMessage, successCreate]);

  const getNoDataAvailable = () => {
    if (!eventErrorMessage?.length && !isLoading && !events.events?.length) {
      return (
        <div className={classes.page}>
          <NoDataAvailable />
        </div>
      );
    }
    return null;
  };

  const getUnableToFetch = () => {
    if (eventErrorMessage?.length && !isLoading) {
      return (
        <div className={classes.page}>
          <UnableToFetch />
        </div>
      );
    }
    return null;
  };

  return (
    <div className={classes['event-list']} data-testid="event-list">
      {renderToast()}
      {getUnableToFetch()}
      {getNoDataAvailable()}
      <Loader show={isLoading || exportLoading || createEventLoading} />
      {!eventErrorMessage && events?.events && events?.events?.length !== 0 && (
        <div>
          {events.events[0]?.eventName !== '' ? (
            <div className={classes['event-list-table-data']}>
              <CITableGrid
                headers={[
                  {
                    name: EventLabels.EVENT_NAME,
                    label: (
                      <div className={classes['render-event-header']}>
                        <Checkbox
                          type="checkbox"
                          id="selectAll"
                          onChange={handleSelectAllCheckbox}
                          checked={isCheckAllEventList}
                          disabled={
                            events.events.length > MAX_SELECTION_SIZE
                            || events?.events.some(
                              (item) =>
                                item.status.toLocaleUpperCase()
                                !== STATUS_TYPES.ACTIVE
                            )
                          }
                          data-testid="selectAllCheckBox"
                        />
                        <div className={classes['header-render']}>
                          {translate('event_list_component.lbl_event_name')}
                        </div>
                      </div>
                    ),
                    sortOptions: {
                      callback: handleClick
                    },
                    headerClassName: 'check-box-input',
                    options: { breakPointWidth: 4 },
                    columnData: {
                      field: EventLabels.EVENT_NAME,
                      render: renderEventBody
                    }
                  },
                  {
                    name: EventLabels.EVENT_STATUS,
                    label: translate('event_list_component.lbl_event_status'),
                    sortOptions: {
                      callback: handleClick
                    },
                    options: { breakPointWidth: 2 },
                    columnData: {
                      field: EventLabels.EVENT_STATUS,
                      render: renderEventStatus
                    }
                  },
                  {
                    name: EventLabels.LAST_MODIFIED,
                    label: translate('event_list_component.lbl_last_modified'),
                    sortOptions: {
                      callback: handleClick
                    },
                    options: { breakPointWidth: 2 },
                    labelOptions: {
                      helperText: `(${getLocaleDateTimeFormat()})`,
                      helperTextClass: 'updatedon-sublabel'
                    },
                    columnData: {
                      field: '',
                      render: renderLastModified
                    }
                  },
                  {
                    name: EventLabels.UPDATED_BY,
                    label: translate('event_list_component.lbl_modified_by'),
                    sortOptions: {
                      callback: handleClick
                    },
                    options: { breakPointWidth: 2 },
                    columnData: {
                      field: EventLabels.UPDATED_BY,
                      render: updatedBy
                    }
                  },
                  {
                    name: EventLabels.ACTION_BTN,
                    label: '',
                    options: { breakPointWidth: 2 },
                    columnData: {
                      field: EventLabels.ACTION_BTN,
                      render: renderActionBtn,
                      className: 'render-action'
                    }
                  }
                ]}
                sorted={{
                  sortedName: selectedSortField,
                  sortedOrder: queryParams.type
                }}
                footer={footerObj}
                tableData={events.events}
              />
            </div>
          ) : null}
        </div>
      )}
      <Modal
        modalTitle={translate('events_wrapper.lbl_modal_version')}
        btnCloseText={String(translate('service_wrapper.lbl_cancel'))}
        btnSaveText={String(translate('service_wrapper.lbl_proceed'))}
        modalBody={modalBody}
        modalShow={showConfirmDialog}
        onCloseModal={handleCloseConfirmDialog}
        onSaveModal={handleConfirmDialog}
        alignToCentre
        disableSaveBtn={false}
        disableCloseBtn={false}
        type="warning"
      />
    </div>
  );
};
